import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { MetadataService } from '../../../core/services/metadata.service';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { EducationAndTrainingDto } from '../../../core/dto/education-and-training';
import { Observable } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { SearchService } from '../../../core/search/search.service';
import { dasherize } from '../../../utils/strings';
import { SearchState } from '../../../core/search/search-state';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { syncControlsWithQueryParams } from '../../../utils/router/sync-controls-with-query-params';
import { syncQueryParamsWithControls } from '../../../utils/router/sync-query-params-with-controls';
import { getToday } from '../../../utils/date';

@UntilDestroy()
@Component({
    selector: 'isav-education-and-trening-list-page',
    templateUrl: './education-and-trening-list-page.html',
    styleUrls: ['./education-and-trening-list-page.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EducationAndTreningListPage implements OnInit {
    form = new UntypedFormGroup({
        extractedText: new UntypedFormControl(null, [Validators.required]),
        country: new UntypedFormControl(null),
        topic: new UntypedFormControl(null),
        organisation: new UntypedFormControl(null),
        department: new UntypedFormControl(null),
        startDate: new UntypedFormControl(getToday()),
        endDate: new UntypedFormControl(null),
        studyField: new UntypedFormControl(null),
        courseLevel: new UntypedFormControl(null),
        education: new UntypedFormControl('all'),
    });

    searchState: Observable<SearchState<EducationAndTrainingDto>>;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private searchService: SearchService<EducationAndTrainingDto>,
        private metaService: MetadataService
    ) {}

    get apiParams(): {
        extractedText: string;
        'topics.id[]': string;
        'organisation.id': string;
        'departments.id[]': string;
        'teachingCountry.id': string;
        'studyField.id': string;
        'courseLevel.id': string;
        'startDate[after]': string;
        'endDate[before]': string;
        education: string;
    } {
        return {
            extractedText: this.form.value.extractedText,
            'topics.id[]': this.form.value.topic,
            'organisation.id': this.form.value.organisation,
            'departments.id[]': this.form.value.department,
            'teachingCountry.id': this.form.value.country,
            'studyField.id': this.form.value.studyField,
            'courseLevel.id': this.form.value.courseLevel,
            'startDate[after]': this.form.value.startDate,
            'endDate[before]': this.form.value.endDate,
            education: this.form.value.education === 'all' ? null : this.form.value.education,
        };
    }

    ngOnInit() {
        this.metaService.setTitle('Education & Training');
        this.searchState = this.searchService.searchState();
        this.route.queryParams.pipe(untilDestroyed(this)).subscribe((queryParams) => {
            syncControlsWithQueryParams(this.form, queryParams);
        });
        this.searchService.updateQuery(this.apiParams);
    }

    search() {
        syncQueryParamsWithControls(this.form, this.router, this.route);
        this.searchService.search({
            ...this.apiParams,
            page: '1',
        });
    }

    resetForm(): void {
        this.form.reset({
            extractedText: null,
            organisation: null,
            department: null,
            topic: null,
            country: null,
            education: 'all',
            startDate: getToday(),
            endDate: null,
            studyField: null,
            courseLevel: null,
        });
    }

    linkTo(educationAndTraining: EducationAndTrainingDto) {
        return ['view', dasherize(educationAndTraining.title || '')];
    }
}
